import React from 'react';
import { useCurrency } from '@/lib/currency-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const currencyFlags = {
  'USD': '🇺🇸',
  'EUR': '🇪🇺',
  'GBP': '🇬🇧',
  'TRY': '🇹🇷',
  'CHF': '🇨🇭',
  'JPY': '🇯🇵',
  'CNY': '🇨🇳',
  'INR': '🇮🇳',
  'AUD': '🇦🇺',
  'CAD': '🇨🇦',
  'RUB': '🇷🇺',
  'SAR': '🇸🇦',
  'AED': '🇦🇪',
  'BHD': '🇧🇭',
  'KWD': '🇰🇼',
  'QAR': '🇶🇦',
  'IQD': '🇮🇶',
  'IRR': '🇮🇷',
  'OMR': '🇴🇲',
  'JOD': '🇯🇴',
  'PKR': '🇵🇰',
  'MYR': '🇲🇾',
};

const currencyNames = {
  'USD': 'US Dollar',
  'EUR': 'Euro',
  'GBP': 'British Pound',
  'TRY': 'Turkish Lira',
  'CHF': 'Swiss Franc',
  'JPY': 'Japanese Yen',
  'CNY': 'Chinese Yuan',
  'INR': 'Indian Rupee',
  'AUD': 'Australian Dollar',
  'CAD': 'Canadian Dollar',
  'RUB': 'Russian Ruble',
  'SAR': 'Saudi Riyal',
  'AED': 'UAE Dirham',
  'BHD': 'Bahraini Dinar',
  'KWD': 'Kuwaiti Dinar',
  'QAR': 'Qatari Riyal',
  'IQD': 'Iraqi Dinar',
  'IRR': 'Iranian Rial',
  'OMR': 'Omani Rial',
  'JOD': 'Jordanian Dinar',
  'PKR': 'Pakistani Rupee',
  'MYR': 'Malaysian Ringgit',
};

export const CurrencySelector = ({ compact = false }) => {
  const { currency, changeCurrency, availableCurrencies, currencySymbols } = useCurrency();

  // Display format: Symbol for symbol currencies, Code for letter-based ones
  const getDisplayText = (curr) => {
    const symbol = currencySymbols[curr];
    // If symbol is same as code or too long, just show code
    if (!symbol || symbol === curr || symbol.length > 2) {
      return curr;
    }
    return symbol;
  };

  // Compact mode for mobile header - show currency symbol + code
  if (compact) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 gap-1 rounded-full"
            data-testid="currency-selector-mobile"
          >
            <span className="text-sm font-medium">{currencySymbols[currency]}</span>
            <span className="text-xs font-medium text-gray-600">{currency}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 max-h-64 overflow-y-auto">
          {availableCurrencies.map((curr) => (
            <DropdownMenuItem
              key={curr}
              onClick={() => changeCurrency(curr)}
              className={`cursor-pointer ${currency === curr ? 'bg-primary/10' : ''}`}
            >
              <span className="mr-2 text-lg">{currencyFlags[curr]}</span>
              <span className="flex-1 text-sm">{curr}</span>
              <span className="text-muted-foreground text-xs">{currencySymbols[curr]}</span>
              {currency === curr && (
                <span className="ml-1 text-primary text-xs">✓</span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-2"
          data-testid="currency-selector"
        >
          <span className="text-lg">{currencyFlags[currency]}</span>
          <span className="hidden sm:inline font-medium">{getDisplayText(currency)}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {availableCurrencies.map((curr) => (
          <DropdownMenuItem
            key={curr}
            onClick={() => changeCurrency(curr)}
            className="cursor-pointer"
          >
            <span className="mr-2 text-lg">{currencyFlags[curr]}</span>
            <span className="flex-1">{currencyNames[curr]}</span>
            <span className="text-muted-foreground ml-2">{currencySymbols[curr]}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
